import { SagaIterator } from 'redux-saga';
import { call, put } from 'redux-saga/effects';
import { EventName, PerformanceMetric } from '../../../constants/telemetry';
import {
    CaptureSnapshotResponse,
    GetDevBoxResponse,
    captureSnapshot,
} from '../../../data/services/data-plane-api/dev-box';
import { ClientError, FailureOperation, isFailureResponse } from '../../../models/common';
import { getDevBoxState } from '../../../utilities/dev-box';
import { createFailureResponseFromAzureCoreFoundationsErrorBodyOrDefault } from '../../../utilities/failure';
import { trackEvent, trackTimedPerformanceMetric } from '../../../utilities/telemetry/channel';
import { createOptionsForDataPlaneResourceMetric } from '../../../utilities/telemetry/helpers';
import { listDevBoxOperations } from '../../actions/dev-box-operation/dev-box-operation-action-creators';
import {
    pollNonTerminalDevBox,
    takeSnapshotDevBoxAccepted,
    takeSnapshotDevBox as takeSnapshotDevBoxActionCreator,
    takeSnapshotDevBoxError,
    takeSnapshotDevBoxFailed,
    takeSnapshotDevBoxSuccess,
} from '../../actions/dev-box/dev-box-action-creators';
import { TakeSnapshotDevBoxAcceptedAction, TakeSnapshotDevBoxAction } from '../../actions/dev-box/dev-box-actions';
import { getAccessToken } from '../../actions/identity/identity-action-creators';
import { GetAccessTokenForDevCenterDataPlanePayload } from '../../actions/identity/identity-actions';
import { listSnapshots } from '../../actions/snapshot/snapshot-action-creators';
import { createSagaError } from '../../effects/create-saga-error';
import { putAndAwait } from '../../effects/put-and-await';
import { takeEvery, takeLeading } from '../../effects/take';
import { AsyncOutcome } from '../../store/common-state';
import { DevBoxState } from '../../store/dev-box-state';

export function* takeSnapshotDevBoxSaga(action: TakeSnapshotDevBoxAction): SagaIterator {
    const startTime = new Date();
    const { meta, payload } = action;
    const { activityId } = meta ?? {};
    const { id } = payload;

    try {
        const accessToken: string = yield putAndAwait(
            getAccessToken(GetAccessTokenForDevCenterDataPlanePayload(), meta)
        );

        const response: CaptureSnapshotResponse = yield call(captureSnapshot, id, accessToken, activityId);

        if (isFailureResponse(response)) {
            yield put(takeSnapshotDevBoxFailed({ failure: response, id }, meta));

            yield call(
                trackTimedPerformanceMetric,
                PerformanceMetric.TakeSnapshotDevBox,
                startTime,
                AsyncOutcome.Failed,
                createOptionsForDataPlaneResourceMetric(id, activityId, response.code)
            );

            return;
        }

        yield put(takeSnapshotDevBoxAccepted({ id }, meta));

        yield call(
            trackTimedPerformanceMetric,
            PerformanceMetric.TakeSnapshotDevBox,
            startTime,
            AsyncOutcome.Success,
            createOptionsForDataPlaneResourceMetric(id, activityId)
        );
    } catch (err) {
        const error: ClientError = yield createSagaError(err, FailureOperation.CaptureSnapshot);
        yield put(takeSnapshotDevBoxError({ error, id }, meta));

        yield call(
            trackTimedPerformanceMetric,
            PerformanceMetric.TakeSnapshotDevBox,
            startTime,
            AsyncOutcome.Error,
            createOptionsForDataPlaneResourceMetric(id, activityId, error.code)
        );
    }
}

export function* takeSnapshotDevBoxAcceptedSaga(action: TakeSnapshotDevBoxAcceptedAction): SagaIterator {
    const { meta, payload } = action;
    const { activityId } = meta ?? {};
    const { id } = payload;

    try {
        const response: GetDevBoxResponse = yield putAndAwait(pollNonTerminalDevBox({ id }, meta));

        if (isFailureResponse(response)) {
            yield put(takeSnapshotDevBoxFailed({ failure: response, id }, meta));
            return;
        }

        const { data } = response;
        const { actionState } = data;
        const state = getDevBoxState(data);

        switch (actionState) {
            case DevBoxState.SnapshotCaptured:
                yield put(listDevBoxOperations({ id }));
                yield put(takeSnapshotDevBoxSuccess({ id, result: data }, meta));
                yield put(listSnapshots({ id }));
                break;
            default:
                // Unexpected state: log cases where we're falling back on the default error message. This means we're
                // in a failed state, but there are no error details.
                if (!data.error) {
                    trackEvent(EventName.DevBoxInFailedStateWithNoError, {
                        activityId,
                        properties: {
                            actionType: action.type,
                            id,
                            provisioningState: data.provisioningState,
                            devBoxState: state,
                        },
                    });
                }

                const failure = createFailureResponseFromAzureCoreFoundationsErrorBodyOrDefault(
                    data.error,
                    FailureOperation.CaptureSnapshot
                );

                yield put(takeSnapshotDevBoxFailed({ failure, id, result: data }, meta));
                break;
        }
    } catch (err) {
        const error: ClientError = yield createSagaError(err, FailureOperation.CaptureSnapshot);
        yield put(takeSnapshotDevBoxError({ error, id }, meta));
    }
}

export function* takeSnapshotDevBoxListenerSaga(): SagaIterator {
    yield takeLeading(takeSnapshotDevBoxActionCreator, takeSnapshotDevBoxSaga);
}

export function* takeSnapshotDevBoxAcceptedListenerSaga(): SagaIterator {
    yield takeEvery(takeSnapshotDevBoxAccepted, takeSnapshotDevBoxAcceptedSaga);
}
